import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["display", "fileInput", "uploadSection", "count", "errors", "imageItem"]
  static values = {
    organizationId: String,
    maxImages: Number
  }

  connect() {
    this.updateGalleryCount()
    this.updateUploadSection()
  }

  handleFileSelection(event) {
    event.stopPropagation();
    if (event.detail?.programmatic) return;

    const files = Array.from(event.target.files)
    if (files.length === 0) return

    // Check if adding these files would exceed the limit
    const currentCount = this.getCurrentImageCount()
    if (currentCount + files.length > this.maxImagesValue) {
      this.showError(`Cannot add ${files.length} images. Maximum allowed is ${this.maxImagesValue} images.`)
      event.target.value = '' // Clear the input
      return
    }

    // Validate each file
    const validFiles = []
    const errors = []

    files.forEach(file => {
      if (!this.isValidImageFile(file)) {
        errors.push(`${file.name}: Invalid file type or size`)
      } else {
        validFiles.push(file)
      }
    })

    if (errors.length > 0) {
      this.showError(errors.join('<br>'))
      event.target.value = '' // Clear the input
      return
    }

    if (validFiles.length > 0) {
      this.uploadFiles(validFiles)
    }
  }

  removeImage(event) {
    const attachmentId = event.currentTarget.dataset.attachmentId
    const imageItem = this.imageItemTargets.find(item => item.dataset.attachmentId === attachmentId)

    if (!confirm('Are you sure you want to remove this image?')) {
      return
    }

    fetch(`/campus/organizations/${this.organizationIdValue}/remove_gallery_image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ attachment_id: attachmentId })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      if (data.success) {
        imageItem.remove()
        this.updateGalleryCount()
        this.updateUploadSection()
        this.clearErrors()
      } else {
        this.showError(data.message || 'Failed to remove image')
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An error occurred while removing the image')
    })
  }

  uploadFiles(files) {
    // Set the files to the existing file input without submitting the form
    const fileInput = this.fileInputTarget

    if (!fileInput) {
      this.showError('File input not found')
      return
    }

    // Create a DataTransfer object to set the files
    const dataTransfer = new DataTransfer()
    files.forEach(file => {
      dataTransfer.items.add(file)
    })
    fileInput.files = dataTransfer.files

    // Trigger change event to ensure the form recognizes the new files
    fileInput.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: {
      programmatic: true
    }}))

    // Show success message and update UI
    this.showSuccess(`${files.length} image(s) selected. Click "Update Organization" to save.`)
    this.updateGalleryCount()
    this.updateUploadSection()
  }

  isValidImageFile(file) {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    const maxSize = 5 * 1024 * 1024 // 5MB

    if (!allowedTypes.includes(file.type)) {
      return false
    }

    if (file.size > maxSize) {
      return false
    }

    return true
  }

  getCurrentImageCount() {
    return this.imageItemTargets.length
  }

  updateGalleryCount() {
    const count = this.getCurrentImageCount()
    const fileInput = this.fileInputTarget
    const selectedFiles = fileInput ? fileInput.files.length : 0

    if (selectedFiles > 0) {
      this.countTarget.textContent = `Current: ${count}/${this.maxImagesValue} images (${selectedFiles} selected)`
    } else {
      this.countTarget.textContent = `Current: ${count}/${this.maxImagesValue} images`
    }
  }

  updateUploadSection() {
    const currentCount = this.getCurrentImageCount()
    const fileInput = this.fileInputTarget
    const selectedFiles = fileInput ? fileInput.files.length : 0
    const totalAfterUpload = currentCount + selectedFiles

    if (currentCount >= this.maxImagesValue) {
      fileInput.disabled = true
      fileInput.parentElement.style.display = 'none'
      this.uploadSectionTarget.insertAdjacentHTML('beforeend', `
        <p class="gallery-status-message" style="color: #666; font-style: italic;">
          Maximum number of gallery images (${this.maxImagesValue}) reached.
        </p>
      `)
    } else if (totalAfterUpload > this.maxImagesValue) {
      fileInput.disabled = true
      fileInput.parentElement.style.display = 'none'
      this.uploadSectionTarget.insertAdjacentHTML('beforeend', `
        <p class="gallery-status-message" style="color: #f57c00; font-style: italic;">
          Selected files would exceed the maximum of ${this.maxImagesValue} images. Please remove some files or images first.
        </p>
      `)
    } else {
      fileInput.parentElement.style.display = 'block'
      fileInput.disabled = false
      fileInput.style.display = 'block'
      const existingMessage = this.uploadSectionTarget.querySelector('.gallery-status-message')

      if (existingMessage) {
        existingMessage.remove()
      }
    }
  }


  showError(message) {
    this.errorsTarget.innerHTML = `
      <div style="background-color: #ffebee; color: #c62828; padding: 10px; border-radius: 4px; border: 1px solid #ffcdd2;">
        ${message}
      </div>
    `
  }

  showSuccess(message) {
    this.errorsTarget.innerHTML = `
      <div style="background-color: #e8f5e8; color: #2e7d32; padding: 10px; border-radius: 4px; border: 1px solid #c8e6c9;">
        ${message}
      </div>
    `
  }

  clearErrors() {
    this.errorsTarget.innerHTML = ''
  }
}
